
import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, X, FileImage } from 'lucide-react';
import { cn } from '@/lib/utils';
import { compressImageToFile } from '@/lib/imageCompression';

interface FileUploadProps {
  onFileSelect: (file: File) => void;
  onFileRemove: () => void;
  selectedFile: File | null;
  accept?: string;
  maxSize?: number; // in MB
  label: string;
  className?: string;
  disabled?: boolean;
  compressImages?: boolean;
}

const FileUpload = ({ 
  onFileSelect, 
  onFileRemove, 
  selectedFile, 
  accept = "image/*", 
  maxSize = 25,
  label,
  className,
  disabled,
  compressImages = true
}: FileUploadProps) => {
  const [dragActive, setDragActive] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processFile = async (file: File) => {
    if (file.size > maxSize * 1024 * 1024) {
      alert(`El archivo debe ser menor a ${maxSize}MB`);
      return;
    }

    if (compressImages && (file.type.startsWith('image/') || file.type === '')) {
      try {
        setIsCompressing(true);
        const compressedFile = await compressImageToFile(file);
        onFileSelect(compressedFile);
      } catch (err) {
        console.warn('Error al comprimir imagen, usando archivo original:', err);
        onFileSelect(file);
      } finally {
        setIsCompressing(false);
      }
    } else {
      onFileSelect(file);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      await processFile(file);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      await processFile(file);
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={cn("flex flex-col space-y-2", className)}>
      <label className="text-sm font-medium">{label}</label>
      
      {!selectedFile ? (
        <div
          className={cn(
            "relative border-2 border-dashed rounded-lg p-6 transition-colors cursor-pointer",
            dragActive ? "border-primary bg-primary/5" : "border-gray-300 hover:border-gray-400",
            disabled && "opacity-50 cursor-not-allowed"
          )}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={openFileDialog}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleFileChange}
            className="hidden"
            disabled={disabled}
          />
          
          <div className="flex flex-col items-center justify-center space-y-2">
            <Upload className="h-8 w-8 text-gray-400" />
            <div className="text-center">
              <p className="text-sm text-gray-600">
                Arrastra y suelta tu archivo aquí o{" "}
                <span className="text-primary font-medium">haz clic para seleccionar</span>
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Máximo {maxSize}MB
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="relative border rounded-lg p-4 bg-gray-50">
          <div className="flex items-center space-x-3">
            <FileImage className="h-8 w-8 text-blue-500" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {selectedFile.name}
              </p>
              <p className="text-xs text-gray-500">
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onFileRemove}
              disabled={disabled}
              className="text-red-500 hover:text-red-700"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
